/**
 * Tab switcher for the contact page (.tx-contact__tabs).
 *
 * Markup contract:
 *   <div class="tx-contact__tabs" role="tablist">
 *     <button class="tx-contact__tab is-active"
 *             aria-controls="tx-contact__panel-general"
 *             data-target="general" …>…</button>
 *     …
 *   </div>
 *   <div class="tx-contact__panel" id="tx-contact__panel-general">…</div>
 *
 * URL hash binding:
 *   (no hash)         → general
 *   #request_quote    → quote
 *   #ask_engineer     → ask
 * Page-load hash + later hashchange events both drive tab selection,
 * and manual tab clicks update the URL hash so the active form is
 * always linkable / shareable.
 *
 * The "Not sure which form?" guide rail (.tx-contact__guide-row) carries
 * the same data-target and routes through the same activator so the rail
 * can drive tab selection from outside the tablist.
 */

const SELECTOR_TABS = '.tx-contact__tabs';
const SELECTOR_TAB  = '.tx-contact__tab';
const SELECTOR_GUIDE_ROW = '.tx-contact__guide-row';

const HASH_TO_TAB = {
    '#request_quote': 'quote',
    '#ask_engineer':  'ask',
};
const TAB_TO_HASH = {
    quote: '#request_quote',
    ask:   '#ask_engineer',
};

const tabFromHash = (hash) => HASH_TO_TAB[hash] || 'general';

const initContactTabs = () => {
    const tablist = document.querySelector(SELECTOR_TABS);
    if (!tablist) return;

    const root   = tablist.closest('.tx-contact__main') || tablist.parentElement;
    const tabs   = Array.from(tablist.querySelectorAll(SELECTOR_TAB));
    if (!root || tabs.length === 0) return;

    const panels = Array.from(root.querySelectorAll('.tx-contact__panel'));

    const syncHash = (targetId) => {
        const desired = TAB_TO_HASH[targetId] || '';
        if (desired === window.location.hash) return;
        const newUrl = desired
            ? window.location.pathname + window.location.search + desired
            : window.location.pathname + window.location.search;
        history.replaceState(null, '', newUrl);
    };

    const activateTarget = (targetId, { focus = false, updateHash = false } = {}) => {
        const targetTab = tabs.find((t) => t.dataset.target === targetId);
        if (!targetTab) return;

        tabs.forEach((t) => {
            const selected = t === targetTab;
            t.classList.toggle('is-active', selected);
            t.setAttribute('aria-selected', selected ? 'true' : 'false');
            t.setAttribute('tabindex', selected ? '0' : '-1');
        });

        panels.forEach((p) => {
            const isMatch = p.id === targetTab.getAttribute('aria-controls');
            if (isMatch) p.removeAttribute('hidden');
            else         p.setAttribute('hidden', '');
        });

        if (updateHash) syncHash(targetId);
        if (focus) targetTab.focus();
    };

    tablist.addEventListener('click', (e) => {
        const btn = e.target.closest(SELECTOR_TAB);
        if (btn && tabs.includes(btn)) {
            activateTarget(btn.dataset.target, { updateHash: true });
        }
    });

    tablist.addEventListener('keydown', (e) => {
        const i = tabs.indexOf(document.activeElement);
        if (i === -1) return;
        let next = null;
        if      (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
        else if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   next = tabs[(i - 1 + tabs.length) % tabs.length];
        else if (e.key === 'Home') next = tabs[0];
        else if (e.key === 'End')  next = tabs[tabs.length - 1];
        else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            activateTarget(document.activeElement.dataset.target, { updateHash: true });
            return;
        } else return;
        e.preventDefault();
        if (next) next.focus();
    });

    // "Not sure which form?" guide rail — clicking a row jumps to its tab
    // and scrolls the form into view so the user sees the switch happen.
    document.querySelectorAll(SELECTOR_GUIDE_ROW).forEach((row) => {
        row.addEventListener('click', () => {
            const target = row.dataset.target;
            if (!target) return;
            activateTarget(target, { focus: true, updateHash: true });
            const panels_root = root.querySelector('.tx-contact__panels');
            if (panels_root && typeof panels_root.scrollIntoView === 'function') {
                panels_root.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        });
    });

    // Initial hash + later hashchange events drive selection without
    // updateHash (it already matches what's in the URL).
    activateTarget(tabFromHash(window.location.hash));
    window.addEventListener('hashchange', () => {
        activateTarget(tabFromHash(window.location.hash));
    });

    // Scoped entry links across the site carry clean query params
    // (?subject=bom|quote|volume|cross-reference|training|partner,
    // ?sku=, ?brand=, ?topic=/?cat=). Route to the right tab and
    // prefill matching fields — never overwriting anything the user
    // already typed. A hash wins over params when both are present.
    const applyEntryParams = () => {
        const params  = new URLSearchParams(window.location.search);
        const subject = (params.get('subject') || '').toLowerCase();
        if (!subject && !params.get('sku') && !params.get('brand')) return;

        const QUOTE_SUBJECTS = ['quote', 'bom', 'volume', 'cross-reference'];
        if (!window.location.hash && subject) {
            activateTarget(QUOTE_SUBJECTS.includes(subject) ? 'quote' : 'general');
        }

        const setVal = (name, value) => {
            const el = document.querySelector(`[name="${name}"]`);
            if (el && el.value === '') el.value = value;
        };

        const sku   = params.get('sku');
        const topic = params.get('topic') || params.get('cat');
        if (sku) setVal('quote-part', sku);
        else if (subject === 'bom') setVal('quote-part', 'SEE ATTACHED BOM / PARTS LIST');
        else if (subject === 'cross-reference') setVal('quote-part', 'CROSS-REFERENCE — part number(s) below');
        else if (topic) setVal('quote-part', `${topic.replace(/[-_]/g, ' ').toUpperCase()} — range inquiry`);

        const brand = (params.get('brand') || '').toLowerCase();
        if (brand) {
            document.querySelectorAll('input[name="quote-brands[]"]').forEach((cb) => {
                if (brand.includes(cb.value.toLowerCase())) cb.checked = true;
            });
        }

        if (subject && !QUOTE_SUBJECTS.includes(subject)) {
            const canned = { training: 'Training — reserve a seat', partner: 'Become a partner' };
            setVal('general-subject', canned[subject] || subject.replace(/[-_]/g, ' '));
        }
    };
    applyEntryParams();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initContactTabs);
} else {
    initContactTabs();
}
